import React from 'react';
import SEO from '@/components/SEO';
import { PageHero } from '@/components/site';
import { AudienceMain } from './audienceParts';
import { LEGAL_LINK, LegalBody } from './legalParts';

/**
 * Privacy Policy (`/privacy`) under SLOT_MODEL. Keeps today's sections (what we
 * collect, how we use it, sharing, your rights, contact) and adds the slot model's
 * cases: offers are claimed with an email only; Premium landing-page lead forms and
 * Venue Sponsor microsites collect details on behalf of that advertiser; advertisers
 * see counted totals in their reports. Today's page names no jurisdiction; neither does
 * this one.
 *
 * Sept 2026 redesign: the package's reading layout (site/privacy/index.html: a table of
 * contents beside the text, legalParts.jsx) with the wording unchanged, word for word
 * (legal.redesign.test.jsx compares it with the wording of September 25, 2026). The
 * wording changes only with the owner's sign-off.
 */

export const PRIVACY_LAST_UPDATED = { label: 'September 25, 2026', iso: '2026-09-25' };

export const PRIVACY_SEO = {
  title: 'Privacy Policy',
  description:
    "Tappd's privacy policy: what we collect when you scan a coaster or claim an offer with your email, how advertiser lead forms work and what advertisers see.",
  url: '/privacy',
};

const MAIL = (
  <a href="mailto:hello@tappd.app" className={LEGAL_LINK}>
    hello@tappd.app
  </a>
);

export const PRIVACY_SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    body: [
      'You can scan a coaster and browse offers without an account. When you scan, we record the scan: which coaster design and venue it came from, and when.',
      'When you claim an offer, we ask only for your email address. We use it to send you the claim code and to record the claim for the business. If you create an account, we also keep your name and the offers you have saved.',
      'If you create a free listing for your business, the business details you enter (name, phone, website and city) are shown publicly on the local deals page.',
    ],
  },
  {
    id: 'lead-forms-and-microsites',
    title: 'Advertiser Lead Forms and Microsites',
    body: [
      'Premium advertisers have their own landing page with a lead form, and Venue Sponsors have their own microsite. When you fill in one of these forms, we collect the details you enter on behalf of that advertiser and pass them to that advertiser so they can reply to you.',
      'We only use those details to deliver your message and to count the lead in the advertiser’s report. Once the advertiser has your details, it handles them under its own privacy policy.',
    ],
  },
  {
    id: 'how-we-use-it',
    title: 'How We Use Your Information',
    body: ['Your information is used to:'],
    points: [
      'Send your claim code and let the business check it when you redeem the offer',
      'Deliver lead form messages to the advertiser you wrote to',
      'Count scans, page visits, claims, redemptions and leads for advertiser reports',
      'Send occasional updates about new offers (you can opt out anytime)',
    ],
  },
  {
    id: 'what-advertisers-see',
    title: 'What We Share with Advertisers',
    body: [
      'We do not sell your personal information.',
      'Advertisers see monthly reports with counted totals: coasters placed, scans, page visits, offer claims, redemptions and leads. These totals do not identify you.',
      'When you claim an offer, we may share your email with the business whose offer you claimed, solely to verify the claim when you redeem it. When you send a lead form, the advertiser receives the details you entered in that form.',
    ],
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: [
      <>You have the right to access, correct, or delete your personal information at any time. Contact us at {MAIL} to exercise these rights.</>,
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    body: [<>If you have questions about this Privacy Policy, please contact us at {MAIL}.</>],
  },
];

export default function PrivacySlot({ lastUpdated = PRIVACY_LAST_UPDATED }) {
  return (
    <AudienceMain>
      <SEO title={PRIVACY_SEO.title} description={PRIVACY_SEO.description} url={PRIVACY_SEO.url} />
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        lede="What we collect when you scan a coaster, claim an offer or contact an advertiser, and what we share."
      />
      <LegalBody lastUpdated={lastUpdated} sections={PRIVACY_SECTIONS} />
    </AudienceMain>
  );
}
